import { css } from '@compiled/react';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';

import { Image } from '@/libraries/design/components/image/src/image';
import wacToken from '@/libraries/design/foundations/token/src/wac-token';

const cornerLogoCss = css({
	position: 'absolute',
	insetBlockStart: '30px',
	insetInlineStart: '40px',
	// Keep the wrapper above sibling content but non-interactive so the empty
	// space around the logo never blocks clicks on the onboarding view
	// underneath. Interactive children (e.g. the back button) opt back in.
	pointerEvents: 'none',
	zIndex: 1,
	display: 'inline-flex',
	alignItems: 'center',
	gap: '8px',
});

const logoImgCss = css({
	width: '96px',
	height: 'auto',
	display: 'block',
});

const backButtonCss = css({
	// Re-enable clicks — the parent wrapper opts out for its whitespace.
	pointerEvents: 'auto',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	// Reset native <button> chrome — we render an icon-only affordance.
	appearance: 'none',
	background: 'transparent',
	border: 'none',
	padding: '4px',
	margin: 0,
	cursor: 'pointer',
	color: wacToken('color.neutral.light700'),
	borderRadius: '4px',
	'&:hover': {
		backgroundColor: wacToken('color.neutral.light200'),
	},
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.extended.blue500')}`,
		outlineOffset: '2px',
	},
});

type CornerLoomLogoProps = {
	/**
	 * When `true`, renders a back chevron button to the left of the wordmark
	 * in the same corner slot. Requires `onBack` to be provided.
	 */
	readonly includeBackButton?: boolean;
	/** Invoked when the back button is clicked. Only used with `includeBackButton`. */
	readonly onBack?: () => void;
	/** Accessible label for the back button. Defaults to "Back". */
	readonly backLabel?: string;
};

/**
 * Fixed Loom wordmark logo pinned to the top-left corner of the Loom
 * onboarding screen. Rendered absolutely so it sits independently of the
 * centered step content: 40px from the left and 30px from the top.
 *
 * Optionally renders a back chevron button to the left of the wordmark
 * (see `includeBackButton`), which naturally pushes the wordmark to the
 * right via the wrapper's flex layout.
 *
 * Assumes the nearest positioned ancestor is the onboarding page layout
 * (the corner is expected to be empty).
 */
export const CornerLoomLogo = ({
	includeBackButton = false,
	onBack,
	backLabel = 'Back',
}: CornerLoomLogoProps = {}) => (
	<div css={cornerLogoCss} data-testid="loom-corner-logo">
		{includeBackButton && onBack && (
			<button
				type="button"
				css={backButtonCss}
				onClick={onBack}
				aria-label={backLabel}
				data-testid="loom-corner-back-button"
			>
				<ChevronLeftIcon label="" color="currentColor" spacing="spacious" />
			</button>
		)}
		<Image
			css={logoImgCss}
			src="https://dam-cdn.atl.orangelogic.com/AssetLink/k3fiwrhg75onexw4rg02bs3a6iov3l4g.svg"
			alt="Loom"
		/>
	</div>
);
